/**
 * Slim in-CMS switcher bar. Rendered inside both AdminHome and UniPortal
 * (top of the page, above the CMS heading) so users who already know
 * they're in a CMS get one-tap access to the sibling dashboard.
 *
 * Hides itself for users without cross-CMS access. Highlights the
 * currently-active dashboard so the switcher is context-aware.
 */
import { Link, useLocation } from "react-router-dom";
import { Shield, School, ChevronRight } from "lucide-react";
import { useCmsRoles } from "@/hooks/useCmsRoles";

export default function CmsSwitcherBar() {
  const { isAdmin, schools, loaded } = useCmsRoles();
  const { pathname } = useLocation();
  if (!loaded) return null;
  if (!isAdmin && (!schools || schools.length === 0)) return null;

  const onAdmin = pathname.startsWith("/admin");
  const onPortal = pathname.startsWith("/uni-portal");

  const items = [];
  if (isAdmin) {
    items.push({
      to: "/admin",
      label: "Platform Admin",
      Icon: Shield,
      active: onAdmin,
      accent: "text-rose-700",
      bg: "bg-rose-50",
    });
  }
  if (schools && schools.length > 0) {
    const first = schools[0];
    const target = schools.length === 1 ? `/uni-portal/uni/${first.id}` : "/uni-portal";
    items.push({
      to: target,
      label: schools.length === 1 ? `${first.name} Portal` : `${schools.length} School Portals`,
      Icon: School,
      active: onPortal,
      accent: "text-emerald-700",
      bg: "bg-emerald-50",
    });
  }

  // If only one item AND we're already on it, don't bother rendering.
  if (items.length === 1 && items[0].active) return null;

  return (
    <div
      data-testid="cms-switcher-bar"
      className="border-b border-stone-200 bg-stone-50/60"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-2 flex items-center gap-3 text-xs">
        <span className="font-underground-sc tracking-wider text-stone-400 shrink-0">
          Switch dashboard →
        </span>
        <div className="flex items-center gap-1.5 flex-wrap">
          {items.map((it) => (
            <Link
              key={it.to}
              to={it.to}
              data-testid={`cms-switcher-${it.to.replace(/\//g, "-").slice(1)}`}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border transition-colors ${
                it.active
                  ? `${it.bg} ${it.accent} border-transparent font-semibold cursor-default`
                  : "bg-white text-stone-600 border-stone-200 hover:border-stone-300 hover:text-stone-900"
              }`}
              onClick={(e) => {
                if (it.active) e.preventDefault();
              }}
            >
              <it.Icon className="h-3 w-3" strokeWidth={2.2} />
              <span>{it.label}</span>
              {it.active && (
                <span className="ml-0.5 text-[9px] font-underground-sc tracking-wider text-stone-500">
                  (you are here)
                </span>
              )}
              {!it.active && <ChevronRight className="h-3 w-3 text-stone-300" strokeWidth={2.5} />}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
